import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';

// Blog post data - in production, this would come from a CMS or database
export const blogPosts = [
  {
    slug: 'calculate-concrete-patio',
    title: 'How to Calculate Concrete for a Patio or Slab',
    excerpt: 'Learn the exact formula for calculating concrete quantities, including waste factors and cost estimates for your next patio project.',
    category: 'Concrete',
    readTime: '5 min read',
    date: '2026-01-05',
    image: '/blog/concrete-patio.jpg',
    imageAlt: 'Fresh concrete patio being poured and smoothed',
    featured: true,
  },
  {
    slug: 'drywall-material-calculator',
    title: 'Drywall Installation: Complete Material Calculator Guide',
    excerpt: 'Everything you need to know about calculating drywall sheets, joint compound, tape, and fasteners for any room.',
    category: 'Drywall',
    readTime: '6 min read',
    date: '2026-01-03',
    image: '/blog/drywall-installation.jpg',
    imageAlt: 'Drywall installation in a residential room',
    featured: true,
  },
  {
    slug: 'paint-calculator-guide',
    title: 'Paint Calculator: How Much Paint Do You Really Need?',
    excerpt: 'Stop over-buying paint! Learn how to calculate the exact gallons needed for walls, ceilings, and trim.',
    category: 'Paint',
    readTime: '4 min read',
    date: '2025-12-28',
    image: '/blog/paint-room.jpg',
    imageAlt: 'Interior room being painted with roller',
    featured: false,
  },
  {
    slug: 'construction-waste-factors',
    title: 'Construction Waste Factors: Why You Need 10-15% Extra Materials',
    excerpt: 'Understanding waste factors is crucial for accurate material estimates. Learn why professionals always order extra.',
    category: 'Tips',
    readTime: '5 min read',
    date: '2025-12-20',
    image: '/blog/construction-materials.jpg',
    imageAlt: 'Stack of construction materials on job site',
    featured: false,
  },
  {
    slug: 'construction-estimating-mistakes',
    title: '5 Common Construction Estimating Mistakes (And How to Avoid Them)',
    excerpt: 'Avoid costly errors on your next project. Learn the top mistakes DIYers and contractors make when estimating materials.',
    category: 'Tips',
    readTime: '7 min read',
    date: '2025-12-15',
    image: '/blog/measuring-tape.jpg',
    imageAlt: 'Measuring tape and construction plans',
    featured: false,
  },
];

const BlogPage = () => {
  const featuredPosts = blogPosts.filter(post => post.featured);

  const getCategoryColor = (category) => {
    const colors = {
      'Concrete': 'bg-gray-100 text-gray-700',
      'Drywall': 'bg-blue-100 text-blue-700',
      'Paint': 'bg-purple-100 text-purple-700',
      'Flooring': 'bg-amber-100 text-amber-700',
      'Lumber': 'bg-green-100 text-green-700',
      'Roofing': 'bg-red-100 text-red-700',
      'Tips': 'bg-indigo-100 text-indigo-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <SEO
        title="Blog"
        description="Construction tips, material calculators guides, and project planning advice from CalcBuildAI. Learn how to estimate materials like a pro."
        canonical="/blog"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-gray-50 py-16">
          <div className="max-w-6xl mx-auto px-4">
            {/* Breadcrumb */}
            <nav className="mb-6 text-sm">
              <ol className="flex items-center gap-2">
                <li><Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link></li>
                <li className="text-gray-400">/</li>
                <li className="text-gray-900 font-medium">Blog</li>
              </ol>
            </nav>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Construction Calculator Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              Tips, guides, and insights to help you estimate materials accurately 
              and complete your construction projects successfully.
            </p>
          </div>
        </section>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <section className="py-12">
            <div className="max-w-6xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Articles</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {featuredPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all"
                  >
                    <div className="h-48 overflow-hidden">
                      {post.image ? (
                        <img 
                          src={post.image} 
                          alt={post.imageAlt || post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                          <span className="text-6xl opacity-50">📐</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{post.excerpt}</p>
                      <div className="flex items-center text-blue-600 font-medium group-hover:gap-2 transition-all">
                        Read article <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Posts */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">All Articles</h2>
            <div className="space-y-4">
              {blogPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col md:flex-row gap-4 p-4 bg-gray-50 hover:bg-blue-50 rounded-xl transition-colors"
                >
                  <div className="md:w-32 flex-shrink-0">
                    {post.image ? (
                      <img 
                        src={post.image} 
                        alt={post.imageAlt || post.title}
                        className="w-full h-20 object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center">
                        <span className="text-2xl opacity-50">📐</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                        {post.category}
                      </span>
                      <span className="text-sm text-gray-500">{post.readTime}</span>
                      <span className="text-sm text-gray-400">•</span>
                      <span className="text-sm text-gray-500">
                        {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-1">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{post.excerpt}</p>
                  </div>
                  <div className="flex items-center text-gray-400 group-hover:text-blue-600 transition-colors">
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Ready to Calculate Your Materials?
            </h2>
            <p className="text-blue-100 mb-6">
              Put these tips into practice with our AI-powered calculator
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Try Free Calculator
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPage;
